import { compactGeometry } from "@project/emerge/src/util/compactGeometry";
import { applyCreasedNormals } from "@project/emerge/src/util/creasedNormals";
import {
  type BufferGeometry,
  EdgesGeometry,
  ExtrudeGeometry,
  type ExtrudeGeometryOptions,
  LineBasicMaterial,
  LineSegments,
  Mesh,
  type MeshStandardMaterial,
  type Shape,
} from "three";
import type { ResourceLease } from "../geometry/FinishedGeometryCache";
import {
  disposeExtrudedAluminumMaterial,
  getAngleSectionAluminumMaterial,
} from "../materials/AluminumProfileMaterial";

export function createAngleSectionLeg(
  shape: Shape,
  settings: ExtrudeGeometryOptions & { bevel: number },
  depth: number,
  strength: number,
  horizontal: boolean,
): ResourceLease<Mesh> {
  const extruded = new ExtrudeGeometry(shape, settings);
  // `applyCreasedNormals` returns the same instance only for non-indexed input,
  // which `ExtrudeGeometry` is today — but the disposal below has to name
  // whichever geometry the mesh ends up wearing, not whichever one was built.
  let geometry: BufferGeometry = extruded;
  let material: MeshStandardMaterial;
  try {
    extruded.translate(0, 0, settings.bevel);
    geometry = applyCreasedNormals(extruded);
    if (geometry !== extruded) extruded.dispose();
    compactGeometry(geometry);
    material = getAngleSectionAluminumMaterial(depth, horizontal);
  } catch (error) {
    geometry.dispose();
    if (geometry !== extruded) extruded.dispose();
    throw error;
  }
  const mesh = new Mesh(geometry, material);
  mesh.position.set(0, -strength, horizontal ? depth - strength : -strength);
  if (horizontal) mesh.rotation.set(-Math.PI / 2, 0, 0);

  let released = false;
  return {
    value: mesh,
    release: () => {
      if (released) return;
      released = true;
      geometry.dispose();
      disposeExtrudedAluminumMaterial(material);
    },
  };
}

export function createAngleSectionEdges(
  mesh: Mesh,
  threshold: number,
): ResourceLease<LineSegments> {
  const geometry = new EdgesGeometry(mesh.geometry, threshold);
  const material = new LineBasicMaterial({ color: "black" });
  const edges = new LineSegments(geometry, material);
  mesh.add(edges);
  let released = false;
  return {
    value: edges,
    release: () => {
      if (released) return;
      released = true;
      mesh.remove(edges);
      geometry.dispose();
      material.dispose();
    },
  };
}
